import React from "react";
import { View, useWindowDimensions, TouchableOpacity } from "react-native";
import Ionicons from "react-native-vector-icons/Ionicons";

import { TTemplate, TTemplateInfo, TSession } from "../../../../utils/types";
import { TitleText, NormalText } from "../../../common/native";
import NavigationProgress from "./NavigationProgress";
import BeforeAfterLandingPage from "./step0/BeforeAfterLandingPage";
import Step1 from "./step1/Step1";
import Step2 from "./step2/Step2";
import Step3 from "./step3/Step3";
import Step4, { TBeforeAfterWorkflowState } from "./step4/Step4";

import {
  TPostSideBySidePairsUploadRequest,
  TPostSideBySidePairsUploadResponse,
  TPostSideBySideUnsortedBatchResponse,
  postSideBySidePairsUpload,
  postSideBySideUnsortedBatch,
} from "../../../../services/api-clinic";
import { SessionContext } from "../../../../contexts";
import {
  convertATemplateWithHeightToAnotherHeight,
  updateTemplateWithClinicLogo,
  validateClinicLogo,
} from "../../../../utils/functions";
import { templates } from "./template";
import { mixpanel } from "../../../../utils/mixpanel";
import { useOnboardingSettings } from "../../../../contexts/OnboardingSettingsContext";
import { useOnboardingTips } from "../../../../contexts/OnboardingTipsContext";
import { useBeforeAfterMockMode } from "./mock/useBeforeAfterMockMode";
import MockModePanel from "./mock/MockModePanel";

// Function to update template text fields with session data
const updateTemplateWithSessionData = (
  template: TTemplate,
  session: TSession,
): TTemplate => {
  if (!template || !session) return template;

  // Clone the template to avoid mutations
  const updatedTemplate = JSON.parse(JSON.stringify(template));

  // Update text fields
  if (updatedTemplate.texts && Array.isArray(updatedTemplate.texts)) {
    updatedTemplate.texts = updatedTemplate.texts.map((text: any) => {
      let updatedContent = text.content;

      // Replace CLINIC NAME with session clinic name
      if (session.clinicName && updatedContent.includes("CLINIC NAME")) {
        updatedContent = updatedContent.replace(
          /CLINIC NAME/g,
          session.clinicName,
        );
      }

      // Replace SURGEON NAME with first surgeon name from session
      if (
        session.surgeons &&
        session.surgeons.length > 0 &&
        updatedContent.includes("SURGEON NAME")
      ) {
        const firstSurgeonName = session.surgeons[0].name;
        updatedContent = updatedContent.replace(
          /SURGEON NAME/g,
          firstSurgeonName,
        );
      }

      return {
        ...text,
        content: updatedContent,
      };
    });
  }

  return updatedTemplate;
};

const HomeBeforeAfterV2: React.FC = () => {
  const { width } = useWindowDimensions();
  // Mock mode docs: src/components/pages/home/before-after-v2/README.md
  const {
    isTestMode,
    scenarioOptions,
    selectedMockScenario,
    setSelectedMockScenario,
    getSelectedScenarioPayload,
  } = useBeforeAfterMockMode();

  const session = React.useContext(SessionContext)?.session;
  if (!session) return <p>Must have a session</p>;

  const [step, setStep] = React.useState<number>(0);
  const [loadingFetchingTemplates, setLoadingFetchingTemplates] =
    React.useState<boolean>(false);
  const [loadingGenerateBeforeAfter, setLoadingGenerateBeforeAfter] =
    React.useState<boolean>(false);
  const [errorFetchingTemplates, setErrorFetchingTemplate] = React.useState<
    string | null
  >(null);
  const [errorBeforeAfter, setErrorBeforeAfter] = React.useState<string | null>(
    null,
  );

  const [processedImageUrls, setProcessedImageUrls] = React.useState<{
    preOp: string[];
    postOp: string[];
  }>({ preOp: [], postOp: [] });

  const [aiSetting, setAiSetting] = React.useState<string[]>([]);
  const [uploadedImages, setUploadedImages] = React.useState<{
    preOp: Blob[];
    postOp: Blob[];
  }>({ preOp: [], postOp: [] });
  const [templatesInfo, setTemplatesInfo] = React.useState<TTemplateInfo[]>([]);
  const [selectedTemplateIndex, setSelectedTemplateIndex] = React.useState<
    number | null
  >(null);
  const [templateEdit, setTemplateEdit] = React.useState<TTemplate | null>(
    null,
  );
  const [beforeAfterResponse, setBeforeAfterResponse] =
    React.useState<TPostSideBySideUnsortedBatchResponse | null>(null);
  const [beforeAfterWorkflowState, setBeforeAfterWorkflowState] =
    React.useState<TBeforeAfterWorkflowState | undefined>(undefined);

  const abortController = React.useRef<AbortController | null>(null);

  // Onboarding tips state
  const onboardingTips = useOnboardingTips();
  const { tipsState, markStepComplete } = useOnboardingTips();

  // Handle the state of showing tips or not
  const { tourActive, showBeforeAfterTips, setShowBeforeAfterTips } =
    useOnboardingSettings();
  const [localTipsToggle, setLocalTipsToggle] = React.useState(false);
  const tipsEnabled = tourActive ? showBeforeAfterTips : localTipsToggle;

  React.useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoadingFetchingTemplates(true);
        const clinicLogoSquare = await validateClinicLogo(session.logoUrl);
        const clinicLogoRectangle = await validateClinicLogo(
          session.logoUrlRectangle,
        );

        const publicTemplatesInfo = templates.map((templateDetail, index) => {
          // First apply logo updates
          let updatedTemplate = updateTemplateWithClinicLogo(
            templateDetail.template,
            clinicLogoSquare,
            clinicLogoRectangle,
          );

          // Then apply text replacements
          updatedTemplate = updateTemplateWithSessionData(
            updatedTemplate,
            session,
          );

          return {
            isPublic: true,
            isDefault: false,
            templateId: index,
            templateDisplayName: templateDetail.templateDisplayName,
            templateName: index.toString(),
            template: updatedTemplate,
          };
        });

        setTemplatesInfo([...publicTemplatesInfo]);
        setSelectedTemplateIndex(0);
        const templatePixelDimention = Math.min(500, width - 20);
        const convertedTemplateSize = convertATemplateWithHeightToAnotherHeight(
          publicTemplatesInfo[0].template,
          templatePixelDimention,
        );
        setTemplateEdit(convertedTemplateSize);
      } catch (error: any) {
        console.log(error.message);
        if (!error?.message.includes("404"))
          setErrorFetchingTemplate("Error fetching templates");
        else setErrorFetchingTemplate("No templates found");
      } finally {
        setLoadingFetchingTemplates(false);
      }
    };

    fetchTemplates();
  }, []);

  const handleGenerateBeforeAfter = async () => {
    setBeforeAfterWorkflowState(undefined);
    setErrorBeforeAfter(null);
    setLoadingGenerateBeforeAfter(true);
    const startTime = Date.now(); // Start time in milliseconds
    try {
      if (isTestMode) {
        const mockPayload = await getSelectedScenarioPayload();
        setProcessedImageUrls(mockPayload.processedImageUrls);
        setBeforeAfterResponse(mockPayload.beforeAfterResponse);
        return;
      }

      abortController.current = new AbortController();
      const { signal } = abortController.current;

      const beforeImageLength = uploadedImages.preOp.length;
      const afterImageLength = uploadedImages.postOp.length;
      const beforeAndAfterImageLength = beforeImageLength + afterImageLength;
      const bodyPostSideBySdiePairsUpload: TPostSideBySidePairsUploadRequest = {
        num_before_images: beforeImageLength,
        num_after_images: afterImageLength,
      };
      const postSideBySidePairsUploadResponse: TPostSideBySidePairsUploadResponse =
        await postSideBySidePairsUpload(
          session,
          bodyPostSideBySdiePairsUpload,
          [...uploadedImages.preOp, ...uploadedImages.postOp],
          signal,
        );

      // The processed RAW images (with backgruond removal and tattoo removal)
      const processedPreOpUrls =
        postSideBySidePairsUploadResponse.processed_presigned_get_urls_before_images;
      const processedPostOpUrls =
        postSideBySidePairsUploadResponse.processed_presigned_get_urls_after_images;
      setProcessedImageUrls({
        preOp: processedPreOpUrls,
        postOp: processedPostOpUrls,
      });
      // const batchId = postSideBySidePairsUploadResponse.batch_uuid;

      const shouldApplyCensorship = aiSetting.includes(
        "is_cover_private_parts",
      );

      const body = {
        template: templatesInfo[0].template,
        is_fix_mode: false,
        predefined_input_images_pairs: [],
        is_remove_face: aiSetting.includes("is_remove_face"),
        is_cover_tattoo: aiSetting.includes("is_cover_tattoo"),
        is_remove_background: aiSetting.includes("is_remove_background"),
        is_cover_private_parts: false,
        ...(shouldApplyCensorship
          ? {
              ai_filters: {
                censorship: {
                  mode: "detect" as const,
                },
              },
            }
          : {}),
        batch_uuid: postSideBySidePairsUploadResponse.batch_uuid,
        after_input_images:
          postSideBySidePairsUploadResponse.after_input_images,
        before_input_images:
          postSideBySidePairsUploadResponse.before_input_images,
      };

      mixpanel.track("Setting Used", {
        "Clinic Name": session?.clinicName ?? "clinic not found",
        "Setting Used": [
          aiSetting.includes("is_remove_face") ? "is_remove_face" : null,
          aiSetting.includes("is_cover_tattoo") ? "is_cover_tattoo" : null,
          aiSetting.includes("is_remove_background")
            ? "is_remove_background"
            : null,
          shouldApplyCensorship ? "censorship_detect" : null,
        ].filter(Boolean),
      });

      const response: TPostSideBySideUnsortedBatchResponse =
        await postSideBySideUnsortedBatch(session, body, signal);
      setBeforeAfterResponse(response);

      const timeTaken = (Date.now() - startTime) / 1000; // Time taken in seconds
      mixpanel.track("Images Generated", {
        "Clinic Name": session?.clinicName ?? "clinic not found",
        "Num Images": beforeAndAfterImageLength,
        "Time to Complete (seconds)": `${timeTaken.toFixed(2)}`,
      });

      console.log("response", response);
    } catch (error: any) {
      if (error?.name === "AbortError") return;
      console.error("Error generating images", error);
      setErrorBeforeAfter("Error generating images");
    } finally {
      setLoadingGenerateBeforeAfter(false);
    }
  };

  const handleSelectTemplate = (index: number) => {
    setSelectedTemplateIndex(index);
    const templatePixelDimention = Math.min(500, width - 20);
    const convertedTemplateSize = convertATemplateWithHeightToAnotherHeight(
      templatesInfo[index].template,
      templatePixelDimention,
    );
    setTemplateEdit(convertedTemplateSize);
  };

  const handleUpdateUploadedImages = (
    images: Blob[],
    type: "preOp" | "postOp",
  ) => {
    setUploadedImages((prev) => ({ ...prev, [type]: images }));
  };

  React.useEffect(() => {
    if (!isTestMode) return;

    let isCancelled = false;

    const createFallbackBlob = (
      kind: "Before" | "After",
      index: number,
      sourceUrl: string,
    ): Blob => {
      const background = kind === "Before" ? "#D9E8FF" : "#DFF7E8";
      const accent = kind === "Before" ? "#1D4ED8" : "#047857";
      const label = `${kind} ${index + 1}`;
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="1000" height="1000" viewBox="0 0 1000 1000">
  <rect width="1000" height="1000" fill="${background}" />
  <rect x="40" y="40" width="920" height="920" rx="24" ry="24" fill="white" stroke="${accent}" stroke-width="8" />
  <text x="500" y="460" text-anchor="middle" font-family="Arial, sans-serif" font-size="72" fill="${accent}" font-weight="700">${label}</text>
  <text x="500" y="540" text-anchor="middle" font-family="Arial, sans-serif" font-size="24" fill="#334155">Mock preload fallback</text>
  <text x="500" y="590" text-anchor="middle" font-family="Arial, sans-serif" font-size="18" fill="#64748B">${sourceUrl}</text>
</svg>`;
      return new Blob([svg], { type: "image/svg+xml" });
    };

    const fetchAsBlobWithFallback = async (
      url: string,
      kind: "Before" | "After",
      index: number,
    ): Promise<Blob> => {
      try {
        const response = await fetch(url, { mode: "cors" });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const blob = await response.blob();
        if (!blob.type.startsWith("image/")) {
          throw new Error(`Unexpected blob type: ${blob.type || "unknown"}`);
        }
        return blob;
      } catch (error) {
        console.warn(
          `[BeforeAfter][MockMode] Failed to fetch mock image (${kind} ${index + 1})`,
          { url, error },
        );
        return createFallbackBlob(kind, index, url);
      }
    };

    const preloadMockImages = async () => {
      try {
        const mockPayload = await getSelectedScenarioPayload();
        const { preOp, postOp } = mockPayload.processedImageUrls;
        console.log("[BeforeAfter][MockMode] Preloading Step 1 images", {
          scenario: selectedMockScenario,
          beforeCount: preOp.length,
          afterCount: postOp.length,
          preOp,
          postOp,
        });

        const [preOpBlobs, postOpBlobs] = await Promise.all([
          Promise.all(
            preOp.map((url, index) =>
              fetchAsBlobWithFallback(url, "Before", index),
            ),
          ),
          Promise.all(
            postOp.map((url, index) =>
              fetchAsBlobWithFallback(url, "After", index),
            ),
          ),
        ]);

        if (isCancelled) return;
        setUploadedImages({ preOp: preOpBlobs, postOp: postOpBlobs });
        console.log("[BeforeAfter][MockMode] Step 1 images preloaded", {
          scenario: selectedMockScenario,
          beforeCount: preOpBlobs.length,
          afterCount: postOpBlobs.length,
        });
      } catch (error) {
        if (isCancelled) return;
        console.error("Failed to preload mock images for Step 1:", error);
      }
    };

    preloadMockImages();

    return () => {
      isCancelled = true;
    };
  }, [getSelectedScenarioPayload, isTestMode, selectedMockScenario]);

  const resetBeforeAfterRun = () => {
    setBeforeAfterWorkflowState(undefined);
    setBeforeAfterResponse(null);
    setProcessedImageUrls({ preOp: [], postOp: [] });
    setErrorBeforeAfter(null);
  };

  const handleStartAgainFresh = () => {
    setUploadedImages({ preOp: [], postOp: [] });
    setAiSetting([]);
    resetBeforeAfterRun();
    setStep(1);
  };

  const handleStartAgainWithImages = () => {
    resetBeforeAfterRun();
    setStep(1);
  };

  // Turn off tips if the user has completed the simulation tips
  React.useEffect(() => {
    if (tipsState.beforeAfterTipsCompleted) {
      setShowBeforeAfterTips(false);
    }
  }, [tipsState.beforeAfterTipsCompleted, setShowBeforeAfterTips]);

  return (
    <View style={{ flex: 1 }}>
      {isTestMode && (
        <MockModePanel
          scenarioOptions={scenarioOptions}
          selectedMockScenario={selectedMockScenario}
          setSelectedMockScenario={setSelectedMockScenario}
        />
      )}
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          paddingHorizontal: 20,
        }}
      >
        <TitleText text="Generate Before-After Images" />
        <TouchableOpacity
          onPress={() => setLocalTipsToggle((v) => !v)}
          style={{ padding: 6 }}
        >
          <Ionicons
            name="information-circle-outline"
            size={24}
            color="#00B7F4"
          />
          <NormalText
            text={localTipsToggle ? "Hide tips" : "Show tips"}
            fontWeight="500"
          />
        </TouchableOpacity>
      </View>

      {step > 0 && (
        <NavigationProgress
          step={step}
          setStep={setStep}
          loading={loadingFetchingTemplates || loadingGenerateBeforeAfter}
        />
      )}

      <View style={{ flex: 1 }}>
        {step === 0 && (
          <BeforeAfterLandingPage setStep={setStep} tipsEnabled={tipsEnabled} />
        )}

        {step === 1 && (
          <Step1
            loading={false}
            setStep={setStep}
            aiSetting={aiSetting}
            setAiSetting={setAiSetting}
            uploadedImages={uploadedImages}
            canGenerateWithoutUploads={isTestMode}
            handleUpdateUploadedImages={handleUpdateUploadedImages}
            handleGenerateBeforeAfter={handleGenerateBeforeAfter}
            tipsEnabled={tipsEnabled}
          />
        )}

        {step === 2 && (
          <Step2
            setStep={setStep}
            error={errorFetchingTemplates}
            loading={loadingFetchingTemplates}
            templatesInfo={templatesInfo}
            selectedTemplateIndex={selectedTemplateIndex}
            handleSelectTemplate={handleSelectTemplate}
          />
        )}

        {step === 3 && templateEdit !== null && (
          <Step3
            setStep={setStep}
            template={templateEdit}
            setTemplateEdit={setTemplateEdit}
            tipsEnabled={tipsEnabled}
          />
        )}

        {step === 4 && templateEdit !== null && (
          <Step4
            error={errorBeforeAfter}
            loading={loadingGenerateBeforeAfter}
            templateEdit={templateEdit}
            uploadedImages={uploadedImages}
            beforeAfterResponse={beforeAfterResponse}
            processedImageUrls={processedImageUrls}
            onStartAgainFresh={handleStartAgainFresh}
            onStartAgainWithImages={handleStartAgainWithImages}
            preservedWorkflowState={beforeAfterWorkflowState}
            onWorkflowStateChange={setBeforeAfterWorkflowState}
            tipsEnabled={tipsEnabled}
          />
        )}
      </View>
    </View>
  );
};

export default HomeBeforeAfterV2;
